// src/clients/RaceClient.ts
import { BaseClient } from './BaseClient';
import { resolveRaceApiBaseUrl } from './locationApiBase';
import type { ActivityOption } from '../models/entitlement/ActivityOption';
import type { SlotOption } from '../models/entitlement/SlotOption';
import type {
  AwaitingGuestPayment, ReleaseHoldResult, ConfirmedGuestPayment,
} from '../models/entitlement/RedeemResult';
import type {
  RaceNight, ClaimRaceNightBody, ClaimRaceNightResult, JoinWaitlistResult, RigClass,
} from '../models/raceNights/RaceNight';
import type {
  SessionQuote, SessionQuoteBody, SessionBookBody, SessionBookResult,
} from '../models/sessions/Session';

interface ExperienceDto { id: string; name: string; activityIds: string[]; }
interface ActivityDto { id: string; title: string; durationInHours: number; }
interface SlotDto {
  slotId: string; startTime: string; endTime: string;
  proRigsAvailable: number; esportsRigsAvailable: number;
  pricePerPersonCents?: number; esportsPricePerPersonCents?: number;
}

const GENERIC_REFUSAL = 'Something went wrong. Please try again.';

/** Every field a refusal can carry. `nextFreesOn`: claim 422 includedLimit. `quote`: book 409 priceChanged. */
interface RefusalBody { error?: string; reason?: string; nextFreesOn?: string | null; quote?: SessionQuote | null; }

/** The refusal body as sent; empty when there is none (or it isn't JSON). */
const readRefusalBody = async (res: Response): Promise<RefusalBody> => {
  try {
    return ((await res.json()) as RefusalBody | null) ?? {};
  } catch {
    return {};
  }
};

/** The server's { error, reason } refusal body; a generic message when there is none. */
const readRefusal = async (res: Response): Promise<{ error: string; reason?: string }> => {
  const parsed = await readRefusalBody(res);
  return { error: parsed.error || GENERIC_REFUSAL, reason: parsed.reason };
};

export class RaceClient extends BaseClient {
  // public funnel GETs are anonymous; going through fetchWithAuth is harmless and consistent.
  public async getActivities(location: string): Promise<ActivityOption[]> {
    const base = resolveRaceApiBaseUrl(location);
    const expRes = await this.fetchWithAuth(`${base}public/experiences`, { method: 'GET' });
    if (!expRes.ok) throw new Error(`Failed to load experiences (${expRes.status})`);
    const experiences = (await expRes.json()) as ExperienceDto[];

    const options: ActivityOption[] = [];
    for (const exp of experiences) {
      const search = new URLSearchParams({ location, experienceId: exp.id });
      const actRes = await this.fetchWithAuth(`${base}public/bookings/activities?${search}`, { method: 'GET' });
      if (!actRes.ok) throw new Error(`Failed to load activities (${actRes.status})`);
      const activities = (await actRes.json()) as ActivityDto[];
      for (const a of activities) {
        options.push({ activityId: a.id, experienceId: exp.id, title: a.title, durationInHours: a.durationInHours });
      }
    }
    return options;
  }

  public async getDayAvailability(location: string, dateYmd: string, activityId: string, people = 1): Promise<SlotOption[]> {
    const base = resolveRaceApiBaseUrl(location);
    const search = new URLSearchParams({ location, date: dateYmd, activityId, people: String(people) });
    const res = await this.fetchWithAuth(`${base}public/bookings/availability/day?${search}`, { method: 'GET' });
    if (!res.ok) throw new Error(`Failed to load availability (${res.status})`);
    const payload = (await res.json()) as { slots?: SlotDto[] };
    return (payload.slots ?? []).map((s) => ({
      slotId: s.slotId, startTime: s.startTime, endTime: s.endTime,
      proRigsAvailable: s.proRigsAvailable, esportsRigsAvailable: s.esportsRigsAvailable,
      // Quoted per-seat rates. Passed through untouched so the guest rows can show what a seat
      // costs; they were previously dropped here, which is why adding a guest changed nothing.
      pricePerPersonCents: s.pricePerPersonCents,
      esportsPricePerPersonCents: s.esportsPricePerPersonCents,
    }));
  }

  /**
   * Promotes a provisional guest booking once its card succeeded. The server re-reads the payment
   * status from Stripe itself — this call is only a nudge, not evidence the money moved.
   * Idempotent: a duplicate confirm is safe and also returns 200.
   *
   * CALLERS MUST NOTE: this can REJECT as well as resolve false — it awaits a bare fetch, so a
   * dropped connection throws. After a card has succeeded, a rejection means exactly what false
   * means: the money moved and the confirmation is unknown. Treat both identically, or a charged
   * customer is left with no message and a live Pay button.
   *
   * `location` must be the venue that HOLDS the booking: WebApi is deployed per venue, so
   * confirming against any other venue hits a DB without the row and gets the uniform
   * 404-shaped refusal.
   *
   * Resolves with the booking's customer-facing confirmation code (WOR-/TRZ-) on success, so the
   * member is never shown the raw booking id as their reference; null when the API sent none.
   */
  public async confirmGuestPayment(
    location: string,
    body: { bookingId: string; intentId: string },
  ): Promise<ConfirmedGuestPayment | false> {
    const base = resolveRaceApiBaseUrl(location);
    const res = await this.fetchWithAuth(`${base}public/bookings/entitlement/confirm`, {
      method: 'POST', body: JSON.stringify(body),
    });
    if (!res.ok) return false;
    // The 200 is what makes the booking real; the code is only its label. An unreadable body
    // must not turn a confirmed booking into a failure.
    let confirmationCode: string | null = null;
    try {
      const json = await res.json();
      if (typeof json?.confirmationCode === 'string' && json.confirmationCode) {
        confirmationCode = json.confirmationCode;
      }
    } catch {
      // empty / non-JSON body: confirmed, without a code
    }
    return { confirmationCode };
  }

  /**
   * Lets go of a provisional guest booking so the member can go back and change it. The server
   * re-reads the intent from Stripe before releasing, so a card that has already gone through
   * comes back as `alreadyPaid` rather than being abandoned.
   *
   * Same auth and same per-venue rule as confirmGuestPayment: `location` is the venue that HOLDS
   * the booking. Releasing against any other venue hits a DB without the row — the 404 would read
   * as "released" while the real hold kept the seats.
   *
   * Like confirmGuestPayment this awaits a bare fetch, so a dropped connection REJECTS. Callers
   * must treat that as `failed`: the hold's state is unknown.
   */
  public async releaseGuestHold(
    location: string,
    body: { bookingId: string; intentId: string },
  ): Promise<ReleaseHoldResult> {
    const base = resolveRaceApiBaseUrl(location);
    const res = await this.fetchWithAuth(`${base}public/bookings/entitlement/release`, {
      method: 'POST', body: JSON.stringify(body),
    });
    // 404: no such hold for this member (a stale stored handle, or already gone). Nothing is
    // holding seats, so it means what a release means.
    if (res.status === 404) return { outcome: 'released' };

    let parsed: { released?: boolean; alreadyPaid?: boolean; error?: string } | null = null;
    try {
      parsed = await res.json();
    } catch {
      // non-JSON / empty body — classified on status alone below
    }
    // Only an explicit `released: true` counts. A 200 without it is not proof the seats are free,
    // and going back over a hold that still exists would leave the member re-booking against it.
    if (res.ok) return parsed?.released === true ? { outcome: 'released' } : { outcome: 'failed' };
    if (res.status === 409) {
      if (parsed?.alreadyPaid === true) return { outcome: 'alreadyPaid' };
      return {
        outcome: 'processing',
        error: parsed?.error || 'Your payment is still processing. Please wait a moment.',
      };
    }
    return { outcome: 'failed' };
  }

  /** The next 60 days of nights at a venue, as the signed-in member sees them. */
  public async listRaceNights(location: string): Promise<RaceNight[]> {
    const base = resolveRaceApiBaseUrl(location);
    const search = new URLSearchParams({ location });
    const res = await this.fetchWithAuth(`${base}public/race-nights?${search}`, { method: 'GET' });
    if (!res.ok) throw new Error(`Failed to load race nights (${res.status})`);
    return (await res.json()) as RaceNight[];
  }

  /**
   * Claims the member's seat (and any guests). Same 202 shape as bookSession, so the card step
   * and its confirm/release are shared. `location` is the venue whose API holds the night.
   */
  public async claimRaceNight(location: string, occurrenceId: string, body: ClaimRaceNightBody): Promise<ClaimRaceNightResult> {
    const base = resolveRaceApiBaseUrl(location);
    const res = await this.fetchWithAuth(`${base}public/race-nights/${occurrenceId}/claim`, {
      method: 'POST', body: JSON.stringify(body),
    });
    // 202 BEFORE res.ok — res.ok is true for 202, and reading it as booked would skip the card.
    if (res.status === 202) {
      const provisional = (await res.json()) as AwaitingGuestPayment;
      return { ok: true, awaitingPayment: true, ...provisional };
    }
    if (res.ok) {
      const json = (await res.json()) as { bookingId: string; confirmationCode?: string | null; newCreditBalance?: number | null };
      return {
        ok: true, bookingId: String(json.bookingId), confirmationCode: json.confirmationCode ?? null,
        newCreditBalance: json.newCreditBalance ?? null,
      };
    }
    const refusal = await readRefusalBody(res);
    return {
      ok: false, status: res.status, error: refusal.error || GENERIC_REFUSAL, reason: refusal.reason,
      ...(refusal.reason === 'includedLimit' ? { nextFreesOn: refusal.nextFreesOn ?? null } : {}),
    };
  }

  /**
   * Each seat's ways to pay and their prices. Books nothing. Null on 404: the slot, activity or session
   * is gone. Any other failure throws, so a broken quote never reads as "that session is gone".
   * `location` goes in the body as well as picking the venue's API, from the one argument.
   */
  public async quoteSession(location: string, body: SessionQuoteBody): Promise<SessionQuote | null> {
    const base = resolveRaceApiBaseUrl(location);
    const res = await this.fetchWithAuth(`${base}public/sessions/quote`, {
      method: 'POST', body: JSON.stringify({ ...body, location }),
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Failed to quote the session (${res.status})`);
    return (await res.json()) as SessionQuote;
  }

  /**
   * Books the session with each seat paid as chosen. Same 202 shape as claimRaceNight, so the
   * card step and its confirm/release are shared. A 409 priceChanged carries
   * the new quote to show before the member books again.
   */
  public async bookSession(location: string, body: SessionBookBody): Promise<SessionBookResult> {
    const base = resolveRaceApiBaseUrl(location);
    const res = await this.fetchWithAuth(`${base}public/sessions/book`, {
      method: 'POST', body: JSON.stringify({ ...body, location }),
    });
    // 202 BEFORE res.ok — res.ok is true for 202, and reading it as booked would skip the card.
    if (res.status === 202) {
      const provisional = (await res.json()) as AwaitingGuestPayment;
      return { ok: true, awaitingPayment: true, ...provisional };
    }
    if (res.ok) {
      const json = (await res.json()) as { bookingId: string; confirmationCode?: string | null; newCreditBalance?: number | null };
      return {
        ok: true,
        booked: {
          bookingId: String(json.bookingId), confirmationCode: json.confirmationCode ?? null,
          newCreditBalance: json.newCreditBalance ?? null,
        },
      };
    }
    const refusal = await readRefusalBody(res);
    return {
      ok: false, status: res.status, error: refusal.error || GENERIC_REFUSAL, reason: refusal.reason,
      ...(refusal.quote ? { quote: refusal.quote } : {}),
    };
  }

  /** Joins a full night's waitlist. The rig preference is advisory for staff. */
  public async joinRaceNightWaitlist(location: string, occurrenceId: string, rigPreference: RigClass): Promise<JoinWaitlistResult> {
    const base = resolveRaceApiBaseUrl(location);
    const res = await this.fetchWithAuth(`${base}public/race-nights/${occurrenceId}/waitlist`, {
      method: 'POST', body: JSON.stringify({ rigPreference }),
    });
    if (res.ok) return { ok: true, position: ((await res.json()) as { position: number }).position };
    return { ok: false, status: res.status, ...(await readRefusal(res)) };
  }

  /** 404 means there is no place to leave, which is what leaving asks for. */
  public async leaveRaceNightWaitlist(location: string, occurrenceId: string): Promise<boolean> {
    const base = resolveRaceApiBaseUrl(location);
    const res = await this.fetchWithAuth(`${base}public/race-nights/${occurrenceId}/waitlist`, { method: 'DELETE' });
    return res.ok || res.status === 404;
  }
}

const raceApi = new RaceClient();
export default raceApi;
